import {
  ApproveJobScopeChangeRequest,
  CreateJobScopeChangeRequest,
  FinalizeJobScopeChangeRequest,
  GetJobScopeChangeRequest,
  GetJobScopeChangeStatusAggregationsRequest,
  ListJobScopeChangesForCollaboratorRequest,
  ListJobScopeChangesRequest,
  RejectJobScopeChangeRequest,
  GetJobScopeChangePriceBreakdownForAdminRequest,
  EditJobScopeChangeItemsRequest,
  JobScopeUpdateRequest,
} from '@taxfyle/api-internal/internal/job_scope_change_pb'
import { JobScopeChangeServicePromiseClient } from '@taxfyle/api-internal/internal/job_scope_change_grpc_web_pb'
import { APIConfig } from 'types/definitions'
import { createGrpcAuthenticator } from 'infra/grpc-authenticator'
import { memoize } from 'lodash'
import { enableDevTools } from 'utils/grpc-util'

/**
 * Creates an instance of the Scope Change Request API.
 *
 * @param config
 */
export function createScopeChangeRequestAPI(config: APIConfig) {
  return new ScopeChangeRequestAPI(config)
}

export class ScopeChangeRequestAPI {
  private readonly jobScopeGrpcClient = createJobScopeChangeGrpcClient(
    this.workDotnetConfig.baseURL,
  )

  private readonly grpcAuthenticator = createGrpcAuthenticator(
    this.workDotnetConfig,
  )

  constructor(private workDotnetConfig: APIConfig) {}

  async updateJobScope(request: JobScopeUpdateRequest) {
    return this.jobScopeGrpcClient.jobScopeUpdate(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  async updateDraftJobScope(request: JobScopeUpdateRequest) {
    return this.jobScopeGrpcClient.draftJobScopeUpdate(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  async listJobScopeChangeRequests(request: ListJobScopeChangesRequest) {
    return this.jobScopeGrpcClient.listJobScopeChanges(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  async finalizeJobScopeChange(request: FinalizeJobScopeChangeRequest) {
    return this.jobScopeGrpcClient.finalizeJobScopeChange(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  async editJobScopeChange(request: EditJobScopeChangeItemsRequest) {
    return this.jobScopeGrpcClient.editJobScopeChangeItems(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  async approveJobScopeChange(request: ApproveJobScopeChangeRequest) {
    return this.jobScopeGrpcClient.approveJobScopeChange(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  async approveJobScopeChangeOnBehalfOfClient(
    request: ApproveJobScopeChangeRequest,
  ) {
    return this.jobScopeGrpcClient.approveJobScopeChangeOnBehalfOfClient(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  async rejectJobScopeChangeOnBehalfOfClient(
    request: RejectJobScopeChangeRequest,
  ) {
    return this.jobScopeGrpcClient.rejectJobScopeChangeOnBehalfOfClient(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  async rejectJobScopeChange(request: RejectJobScopeChangeRequest) {
    return this.jobScopeGrpcClient.rejectJobScopeChange(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  async getJobScopeChange(request: GetJobScopeChangeRequest) {
    return this.jobScopeGrpcClient.getJobScopeChange(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  async getJobScopeChangePriceBreakdownForAdmin(
    request: GetJobScopeChangePriceBreakdownForAdminRequest,
  ) {
    return this.jobScopeGrpcClient.getJobScopeChangePriceBreakdownForAdmin(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  async getScopeChangeRequestStatusAggregations(
    request: GetJobScopeChangeStatusAggregationsRequest,
  ) {
    return this.jobScopeGrpcClient.getJobScopeChangeStatusAggregations(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  async requestScopeChanges(request: CreateJobScopeChangeRequest) {
    return this.jobScopeGrpcClient.createJobScopeChange(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  async listScopeChangesForCollaborator(
    request: ListJobScopeChangesForCollaboratorRequest,
  ) {
    return this.jobScopeGrpcClient.listJobScopeChangesForCollaborator(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  async listScopeChanges(request: ListJobScopeChangesRequest) {
    return this.jobScopeGrpcClient.listJobScopeChanges(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }
}

const createJobScopeChangeGrpcClient = memoize(
  function createJobScopeChangeGrpcClient(baseUrl: string) {
    const client = new JobScopeChangeServicePromiseClient(baseUrl)
    enableDevTools(client)
    return client
  },
)
